import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuth } from '../hooks/authContext'
import { useBrands, useProducts, useSubscriptions, useAnalytics } from '../hooks/useApi'
import { getCatalogUrl, getPublicCatalogHref } from '../lib/catalogUrl'
import { getPlanDisplayName, normalizePlanId, PLAN_FEATURES, canUseFeature, canAccessMarketingCenter, formatPlanLimit, resolveBrandLimit, resolveProductLimit } from '../lib/plans'
import {
  Building2,
  Package,
  CreditCard,
  Plus,
  TrendingUp,
  Users,
  ShoppingCart,
  ChevronRight,
  ExternalLink,
  Check,
  BarChart3,
  Crown,
  Lock,
  Megaphone,
} from 'lucide-react'
import { Card, CardContent } from '../components/ui/Card'

interface StatCardProps {
  title: string
  value: number | string
  icon: React.ReactNode
  trend?: string
  trendUp?: boolean
  color: string
  linkTo?: string
  locked?: boolean
  lockedHint?: string
}

function StatCard({ title, value, icon, trend, trendUp, color, linkTo, locked, lockedHint }: StatCardProps) {
  if (locked) {
    return (
      <div className="bg-gray-100 rounded-xl p-6 text-gray-600 shadow-md border border-dashed border-gray-300 relative overflow-hidden">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-500 text-sm font-medium">{title}</p>
            <p className="text-lg font-bold mt-1 flex items-center gap-2 text-gray-500">
              <Lock className="w-5 h-5" />
              Premium
            </p>
            <p className="text-xs text-gray-500 mt-2">{lockedHint}</p>
          </div>
          <div className="bg-gray-200 p-3 rounded-lg opacity-60">{icon}</div>
        </div>
        <Link to="/subscription" className="mt-4 flex items-center text-sm text-indigo-600 hover:text-indigo-800 font-medium">
          Upgrade Paket <ChevronRight className="w-4 h-4 ml-1" />
        </Link>
      </div>
    )
  }

  return (
    <div className={`${color} rounded-xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-white/80 text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
          {trend && (
            <p className={`text-sm mt-2 flex items-center gap-1 ${trendUp ? 'text-green-300' : 'text-red-300'}`}>
              <TrendingUp className="w-4 h-4" />
              {trend}
            </p>
          )}
        </div>
        <div className="bg-white/20 p-3 rounded-lg">{icon}</div>
      </div>
      {linkTo && (
        <Link to={linkTo} className="mt-4 flex items-center text-sm text-white/80 hover:text-white transition-colors">
          Lihat Detail <ChevronRight className="w-4 h-4 ml-1" />
        </Link>
      )}
    </div>
  )
}

function QuickAction({ icon, label, onClick, color }: { icon: React.ReactNode; label: string; onClick: () => void; color: string }) {
  return (
    <button
      onClick={onClick}
      className={`${color} p-4 rounded-xl text-white shadow-md hover:shadow-lg transition-all hover:scale-105 flex flex-col items-center gap-2`}
    >
      {icon}
      <span className="text-sm font-medium">{label}</span>
    </button>
  )
}

export default function Dashboard() {
  const { user } = useAuth()
  const { data: brands } = useBrands()
  const { data: products } = useProducts()
  const { data: subscriptions } = useSubscriptions()
  const currentSub = subscriptions?.[0]
  const isPlanActive = currentSub?.isActive !== false
  const planId = normalizePlanId(isPlanActive ? currentSub?.plan : 'free')
  const planName = getPlanDisplayName(isPlanActive ? currentSub?.plan : 'free')
  const features = currentSub?.features || PLAN_FEATURES[planId]
  const showAnalytics = canUseFeature(planId, 'analytics')
  const { data: analytics } = useAnalytics(showAnalytics)
  const navigate = useNavigate()
  const maxProducts = resolveProductLimit(isPlanActive ? currentSub?.productLimit : undefined, planId)
  const maxBrands = resolveBrandLimit(isPlanActive ? currentSub?.brandLimit : undefined, planId)
  const canWriteArticles = canUseFeature(planId, 'articles')
  const showMarketing = canAccessMarketingCenter(planId)

  const planCardClass =
    planId === 'premium'
      ? 'border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50'
      : planId === 'basic'
        ? 'border-indigo-300 bg-gradient-to-r from-indigo-50 to-blue-50'
        : 'border-gray-200 bg-gradient-to-r from-gray-50 to-white'

  const primaryBrand = brands?.[0]
  const catalogLink = primaryBrand?.slug
    ? getPublicCatalogHref({
        slug: primaryBrand.slug,
        subdomain: primaryBrand.subdomain,
        plan: planId,
      })
    : null

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-6 text-white shadow-lg">
        <h1 className="text-2xl font-bold mb-2">Selamat Datang, {user?.name}! 👋</h1>
        <p className="text-blue-100">Kelola katalog usaha Anda dengan mudah melalui dashboard ini.</p>
        {user?.referralCode && (
          <button
            type="button"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(user.referralCode!)
                toast.success('Kode referral disalin')
              } catch {
                toast.error('Gagal menyalin kode')
              }
            }}
            className="mt-4 inline-block rounded-lg border border-white/30 bg-white/10 px-4 py-2 text-left hover:bg-white/20"
          >
            <p className="text-xs text-blue-100">Kode referral Anda</p>
            <p className="font-mono text-sm font-bold">{user.referralCode}</p>
          </button>
        )}
      </div>

      {/* Paket Aktif Card */}
      <Card className={`${planCardClass} shadow-sm`}>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Paket Aktif</p>
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                {planId === 'premium' && <Crown className="w-6 h-6 text-amber-500" />}
                {planName}
              </h2>
              {currentSub?.endDate && (
                <p className="text-sm text-gray-500 mt-1">
                  Berlaku hingga {new Date(currentSub.endDate).toLocaleDateString('id-ID')}
                </p>
              )}
            </div>
            {planId !== 'premium' && (
              <Link
                to="/subscription"
                className="inline-flex items-center gap-1 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700"
              >
                Upgrade Paket <ChevronRight className="w-4 h-4" />
              </Link>
            )}
          </div>
          <ul className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {features.map((f, i) => (
              <li key={i} className="flex items-center text-sm text-gray-700">
                <Check className="w-4 h-4 mr-2 text-green-500 shrink-0" />
                {f}
              </li>
            ))}
          </ul>
          <div className="mt-4 pt-4 border-t border-indigo-100 grid sm:grid-cols-2 gap-3 text-sm">
            <div className="rounded-lg bg-white/80 px-3 py-2 border border-indigo-100">
              <span className="text-gray-500">Katalog: </span>
              <span className="font-semibold text-gray-900">
                {brands?.length ?? 0} / {formatPlanLimit(maxBrands)}
              </span>
            </div>
            <div className="rounded-lg bg-white/80 px-3 py-2 border border-indigo-100">
              <span className="text-gray-500">Produk: </span>
              <span className="font-semibold text-gray-900">
                {products?.length ?? 0} / {formatPlanLimit(maxProducts)}
                {Number.isFinite(maxProducts) && (products?.length ?? 0) >= maxProducts && (
                  <span className="text-amber-600 ml-1">(limit)</span>
                )}
              </span>
            </div>
            <div className="rounded-lg bg-white/80 px-3 py-2 border border-indigo-100">
              <span className="text-gray-500">Artikel usaha: </span>
              <span className={`font-semibold ${canWriteArticles ? 'text-green-700' : 'text-gray-500'}`}>
                {canWriteArticles ? 'Tersedia' : 'Basic+'}
              </span>
            </div>
            <div className="rounded-lg bg-white/80 px-3 py-2 border border-indigo-100">
              <span className="text-gray-500">Statistik: </span>
              <span className={`font-semibold ${showAnalytics ? 'text-green-700' : 'text-gray-500'}`}>
                {showAnalytics ? 'Tersedia' : 'Premium'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {showMarketing && (
        <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold text-indigo-900 flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-indigo-600" />
                  Marketing Center
                </h3>
                <p className="text-sm text-indigo-800 mt-2 leading-relaxed">
                  Buat banner promosi, campaign lengkap, caption AI, dan bagikan materi promosi dari satu dashboard.
                </p>
              </div>
              <Link
                to="/marketing"
                className="inline-flex items-center justify-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-indigo-700 shrink-0"
              >
                Buka Marketing Center <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {planId === 'premium' && (
        <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 shadow-sm">
          <CardContent className="pt-6">
            <h3 className="text-lg font-bold text-emerald-900 flex items-center gap-2">
              <Crown className="w-5 h-5 text-emerald-600" />
              SEO Produk Aktif
            </h3>
            <p className="text-sm text-emerald-800 mt-2 leading-relaxed">
              Setiap produk memiliki halaman SEO sendiri dan dapat dimasukkan ke sitemap untuk membantu pengindeksan mesin pencari.
            </p>
            {primaryBrand?.slug && (
              <p className="text-xs text-emerald-700 mt-3">
                Contoh URL:{' '}
                <span className="font-mono bg-white/70 px-2 py-0.5 rounded border border-emerald-200">
                  /catalog/{primaryBrand.slug}/nama-produk
                </span>
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Katalog"
          value={brands?.length || 0}
          icon={<Building2 className="w-8 h-8" />}
          color="bg-gradient-to-br from-blue-500 to-blue-600"
          linkTo="/brand"
        />
        <StatCard
          title="Total Produk"
          value={products?.length || 0}
          icon={<Package className="w-8 h-8" />}
          color="bg-gradient-to-br from-emerald-500 to-emerald-600"
          linkTo="/products"
        />
        <StatCard
          title="Pengunjung Katalog"
          value={showAnalytics ? (analytics?.catalogViews ?? brands?.reduce((s, b) => s + (b.viewCount || 0), 0) ?? 0) : 0}
          icon={<Users className="w-8 h-8" />}
          color="bg-gradient-to-br from-violet-500 to-violet-600"
          locked={!showAnalytics}
          lockedHint="Statistik detail hanya untuk paket Premium"
        />
        <StatCard
          title="Paket"
          value={planName}
          icon={<CreditCard className="w-8 h-8" />}
          color={planId === 'premium' ? 'bg-gradient-to-br from-amber-500 to-amber-600' : 'bg-gradient-to-br from-gray-500 to-gray-600'}
          linkTo="/subscription"
        />
      </div>

      {showAnalytics && analytics && (
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-600" />
              Statistik Pengunjung (Premium)
            </h3>
            <div className="grid sm:grid-cols-3 gap-4 mb-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">Total Pengunjung</p>
                <p className="text-2xl font-bold">{analytics.totalVisitors?.toLocaleString()}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">Kunjungan Katalog</p>
                <p className="text-2xl font-bold">{analytics.catalogViews?.toLocaleString()}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">Klik WhatsApp</p>
                <p className="text-2xl font-bold">{analytics.whatsappClicks?.toLocaleString()}</p>
              </div>
            </div>
            {analytics.topProducts?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Produk Paling Sering Dilihat</p>
                <ul className="space-y-1">
                  {analytics.topProducts.slice(0, 5).map((p: any) => (
                    <li key={p.id} className="text-sm text-gray-600 flex justify-between">
                      <span>{p.name}</span>
                      <span>{p.views} views</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Aksi Cepat</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <QuickAction icon={<Plus className="w-6 h-6" />} label="Kelola Katalog" onClick={() => navigate('/brand')} color="bg-blue-500" />
          <QuickAction icon={<Plus className="w-6 h-6" />} label="Tambah Produk" onClick={() => navigate('/products')} color="bg-emerald-500" />
          <QuickAction
            icon={<ShoppingCart className="w-6 h-6" />}
            label="Lihat Katalog"
            onClick={() => {
              if (catalogLink) window.open(catalogLink, '_blank')
              else toast.error('Buat katalog usaha terlebih dahulu')
            }}
            color="bg-violet-500"
          />
          <QuickAction icon={<CreditCard className="w-6 h-6" />} label="Kelola Paket" onClick={() => navigate('/subscription')} color="bg-amber-500" />
          {showMarketing && (
            <QuickAction icon={<Megaphone className="w-6 h-6" />} label="Marketing Center" onClick={() => navigate('/marketing')} color="bg-indigo-500" />
          )}
        </div>
        {catalogLink && (
          <a href={catalogLink} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800">
            <ExternalLink className="w-4 h-4" />
            Buka katalog publik
          </a>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Panduan Memulai</h3>
          <div className="space-y-3">
            <div className={`flex items-center gap-3 p-3 rounded-lg ${brands?.length ? 'bg-green-50' : 'bg-gray-50'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${brands?.length ? 'bg-green-500 text-white' : 'bg-gray-300'}`}>
                {brands?.length ? '✓' : '1'}
              </div>
              <span className={brands?.length ? 'text-green-700 line-through' : 'text-gray-600'}>Buat katalog usaha pertama</span>
            </div>
            <div className={`flex items-center gap-3 p-3 rounded-lg ${products?.length ? 'bg-green-50' : 'bg-gray-50'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${products?.length ? 'bg-green-500 text-white' : 'bg-gray-300'}`}>
                {products?.length ? '✓' : '2'}
              </div>
              <span className={products?.length ? 'text-green-700 line-through' : 'text-gray-600'}>Tambahkan produk</span>
            </div>
            <div className={`flex items-center gap-3 p-3 rounded-lg ${currentSub ? 'bg-green-50' : 'bg-gray-50'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentSub ? 'bg-green-500 text-white' : 'bg-gray-300'}`}>
                {currentSub ? '✓' : '3'}
              </div>
              <span className={currentSub ? 'text-green-700 line-through' : 'text-gray-600'}>Paket Gratis sudah aktif otomatis</span>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl shadow-sm p-6 border border-orange-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">💡 Tips Sukses</h3>
          <ul className="space-y-3 text-sm text-gray-600">
            <li className="flex items-start gap-2"><span className="text-orange-500">•</span>Tambahkan foto produk berkualitas agar lebih menarik pelanggan</li>
            <li className="flex items-start gap-2"><span className="text-orange-500">•</span>Bagikan link katalog ke WhatsApp dan media sosial</li>
            <li className="flex items-start gap-2"><span className="text-orange-500">•</span>Upgrade ke Premium untuk subdomain usaha sendiri dan statistik pengunjung</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
